"use client";

import React, { useRef } from "react";
import Image from "next/image";
import { useGSAP } from "@gsap/react";
import hasFadeAnim from "@/lib/animation/hasFadeAnim";
import hasTextMovAnim from "@/lib/animation/hasTextMovAnim";
import hasCharAnim from "@/lib/animation/hasCharAnim";

type Props = {
  image: string;
  image_alt?: string;
  title: string;
  paragraphs: string[];
  closing_prefix?: string;
  closing_highlight?: string;
};

const ThePeople = ({
  image,
  image_alt,
  title,
  paragraphs,
  closing_prefix,
  closing_highlight,
}: Props) => {
  // Split judul pada kata terakhir supaya baris pertama & kedua terbentuk
  // e.g. "The People Behind TIGAR" → ["The People", "Behind TIGAR"]
  const titleParts = title.split(" ");
  const midIdx = Math.ceil(titleParts.length / 2);
  const titleLine1 = titleParts.slice(0, midIdx).join(" ");
  const titleLine2 = titleParts.slice(midIdx).join(" ");

  const containerRef = useRef<HTMLDivElement>(null!);

  useGSAP(
    () => {
      hasFadeAnim();
      hasTextMovAnim();
      hasCharAnim();
    },
    { scope: containerRef },
  );

  return (
    <section
      className="w-full bg-white text-black section-spacing-top"
      ref={containerRef}
    >
      <div className="inner-container-2">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 lg:gap-20">
          {/* Kolom Kiri — Gambar mengikuti tinggi kolom kanan */}
          <div className="relative w-full overflow-hidden rounded-sm md:col-span-6 min-h-[400px] md:min-h-[500px]">
            <Image
              src={image}
              alt={image_alt ?? title}
              fill
              className="object-cover object-center !h-auto"
              priority
            />
          </div>

          {/* Kolom Kanan — Teks */}
          <div className="flex flex-col gap-6 md:col-span-6">
            {/* Judul */}
            <h2 className="text-[32px] md:text-[40px] lg:text-[48px] font-light leading-[1.2] tracking-tight text-black has_text_mov_anim">
              {titleLine1}
              {titleLine2 && (
                <>
                  <br />
                  {titleLine2}
                </>
              )}
            </h2>

            {/* Paragraf-paragraf */}
            <div className="flex flex-col gap-5 mt-2">
              {paragraphs?.map((para, i) => (
                <p
                  key={i}
                  className="has_fade_anim text-[18px] sm:text-[20px] lg:text-[22px] leading-relaxed text-black/80 font-light text-justify"
                  data-fade-from="bottom"
                  data-delay="0.10"
                  data-duration="0.90"
                  data-stagger="0.10"
                >
                  {para}
                </p>
              ))}

              {/* Kalimat penutup */}
              {(closing_prefix || closing_highlight) && (
                <p className="has_fade_anim text-[18px] sm:text-[20px] lg:text-[22px] leading-relaxed text-black/80 font-light text-justify">
                  {closing_prefix && (
                    <>
                      {closing_prefix}
                      <br />
                    </>
                  )}
                  {closing_highlight && (
                    <span
                      className="font-bold has_char_anim"
                      data-delay="0.20"
                      data-duration="0.70"
                      data-stagger="0.020"
                    >
                      {closing_highlight}
                    </span>
                  )}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ThePeople;
